import React from "react";
import { getProductKeyFromHandle, getCollectionLandingPagePath } from "./shop";
import { SLEEP_HOMEPAGE_RESTORE_FEATURES } from "./homepageConstants";
import { REST_FEATURES } from "./restFamilyPageConstants";
import {
  REST_KEY,
  RESTORE_KEY,
  HATCH_PRODUCT_HANDLES,
} from "./productConstants";
import Paragraph from "../components/Atoms/Paragraph";

// Collection Handles
export const OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_HANDLE = "out-like-a-light";
export const EIGHT_STRAIGHT_SLEEP_COLLECTION_HANDLE = "eight-straight";
export const EARLY_BIRD_SLEEP_COLLECTION_HANDLE = "early-bird";
export const THE_LITTLES_SLEEP_COLLECTION_HANDLE = "the-littles";

// Collection Keys
export const HATCH_COLLECTION_KEY = "hatchCollection";
export const ADULTS_COLLECTION_KEY = "adults";
export const SLEEP_COLLECTION_KEY = "sleepCollection";
export const OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_KEY = getProductKeyFromHandle(
  OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_HANDLE
);
export const EIGHT_STRAIGHT_SLEEP_COLLECTION_KEY = getProductKeyFromHandle(
  EIGHT_STRAIGHT_SLEEP_COLLECTION_HANDLE
);
export const EARLY_BIRD_SLEEP_COLLECTION_KEY = getProductKeyFromHandle(
  EARLY_BIRD_SLEEP_COLLECTION_HANDLE
);
export const THE_LITTLES_SLEEP_COLLECTION_KEY = getProductKeyFromHandle(
  THE_LITTLES_SLEEP_COLLECTION_HANDLE
);

// Misc
export const SLEEP_GOAL_COLLECTION_QUERY_SEARCH = "handle:sleep goal";

export const COLLECTION_HANDLES = {
  [HATCH_COLLECTION_KEY]: "hatch-collection",
  [SLEEP_COLLECTION_KEY]: "sleep-collection",
};

export const FEATURED_PRODUCTS = {
  [RESTORE_KEY]: {
    features: SLEEP_HOMEPAGE_RESTORE_FEATURES,
  },
  [REST_KEY]: {
    features: REST_FEATURES.slice(0, 3),
  },
};

// Sleep Goal Collection Data
export const SLEEP_GOAL_COLLECTION_DATA = {
  [OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_KEY]: {
    handle: OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_HANDLE,
    collectionKey: OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_KEY,
    secondaryTitle: "For falling asleep fast",
    subNavTitle: "Fall asleep fast",
    url: getCollectionLandingPagePath(OUT_LIKE_A_LIGHT_SLEEP_COLLECTION_HANDLE),
    styles: {
      sectionBackground: "#F5F7FA",
      bannerBackground: "#596EA1",
    },
    imgixProps: {
      tablet: { "fp-y": "0.5", crop: "focalpoint", fit: "crop" },
    },
    introText: {
      header: "Close your eyes and shut it down.",
      body: (
        <Paragraph>
          No running through mental to-do lists. No final phone checks. No need
          to count sheep. Once your head hits the pillow, it’s all over.
        </Paragraph>
      ),
    },
    featuredProduct: {
      handle: HATCH_PRODUCT_HANDLES[RESTORE_KEY],
      intro: "Wind down your way with Hatch Restore",
      image: {
        src: "dream-shop-category-restore-phone-appscreens-routine.png",
        alt: "Hatch Restore",
        sizes: { default: "90vw", tablet: "50vw" },
      },
      ...FEATURED_PRODUCTS[RESTORE_KEY],
    },
    editorial: `
      We’re more mindful than ever at bedtime — practicing good sleep hygiene,
      putting our phones away, and listening to our favorite snoozy meditations
      and stories through Restore to wind down.
    `,
  },
  [EIGHT_STRAIGHT_SLEEP_COLLECTION_KEY]: {
    handle: EIGHT_STRAIGHT_SLEEP_COLLECTION_HANDLE,
    collectionKey: EIGHT_STRAIGHT_SLEEP_COLLECTION_KEY,
    secondaryTitle: "For uninterrupted sleep",
    subNavTitle: "Uninterrupted sleep",
    url: getCollectionLandingPagePath(EIGHT_STRAIGHT_SLEEP_COLLECTION_HANDLE),
    styles: {
      sectionBackground: "#FBF2F0",
      bannerBackground: "#DB6A6E",
    },
    imgixProps: {
      desktop: { "fp-y": "0.55", crop: "focalpoint", fit: "crop" },
    },
    introText: {
      header: "The dream.",
      body: (
        <Paragraph>
          When you close your eyes and don’t open them again until morning. No
          tossing. No turning. Just eight straight hours of blissful,
          refreshing, uninterrupted sleep.
        </Paragraph>
      ),
    },
    featuredProduct: {
      handle: HATCH_PRODUCT_HANDLES[RESTORE_KEY],
      intro: "Snooze through the noise with Hatch Restore",
      image: {
        src: "dream-shop-category-restore-phone-appscreens-sleep.png",
        alt: "Hatch Restore",
        sizes: { default: "90vw", tablet: "50vw" },
      },
      ...FEATURED_PRODUCTS[RESTORE_KEY],
    },
    editorial: `
      Sleep disruptions, be gone! We’re drowning out the noises that wake us in
      the middle of the night with soothing sleep sounds like water, wind,
      waves, and white noise.
    `,
  },
  [EARLY_BIRD_SLEEP_COLLECTION_KEY]: {
    handle: EARLY_BIRD_SLEEP_COLLECTION_HANDLE,
    collectionKey: EARLY_BIRD_SLEEP_COLLECTION_KEY,
    secondaryTitle: "For waking up refreshed",
    subNavTitle: "Wake up refreshed",
    url: getCollectionLandingPagePath(EARLY_BIRD_SLEEP_COLLECTION_HANDLE),
    styles: {
      sectionBackground: "#FAF6EE",
      bannerBackground: "#CDA052",
    },
    introText: {
      header: "Get that worm.",
      body: (
        <>
          <Paragraph>
            Early morning runs. A traffic-free commute. There’s magic in the
            early morning hours.
          </Paragraph>
        </>
      ),
    },
    featuredProduct: {
      handle: HATCH_PRODUCT_HANDLES[RESTORE_KEY],
      intro: "Wake to your own sunrise with Hatch Restore",
      image: {
        src: "dream-shop-category-restore-phone-appscreens-sunrise.png",
        alt: "Hatch Restore",
        sizes: { default: "90vw", tablet: "50vw" },
      },
      ...FEATURED_PRODUCTS[RESTORE_KEY],
    },
    editorial: `
      We’re resetting our circadian rhythms with the warm glow of our sunrise
      alarm. Set to wake us naturally at 6 a.m., we’re up before the kids to
      drink hot coffee or get in a workout before the day begins.
    `,
  },
  [THE_LITTLES_SLEEP_COLLECTION_KEY]: {
    handle: THE_LITTLES_SLEEP_COLLECTION_HANDLE,
    collectionKey: THE_LITTLES_SLEEP_COLLECTION_KEY,
    secondaryTitle: <>Sleep for babies &amp; kids</>,
    subNavTitle: "Sleep for the kids",
    url: getCollectionLandingPagePath(THE_LITTLES_SLEEP_COLLECTION_HANDLE),
    styles: {
      sectionBackground: "#F3F4F6",
      bannerBackground: "#ffffff",
    },
    imgixProps: {
      desktop: { "fp-y": "0.55", crop: "focalpoint", fit: "crop" },
    },
    introText: {
      header: "Snug as bugs in rugs.",
      body: (
        <Paragraph>
          Dreamy midnight feedings. Snoozy long naps. Mornings spent in bed.
          Because when the babies (and toddlers and big kids) sleep, everybody
          sleeps.
        </Paragraph>
      ),
    },
    featuredProductsIntroText: <>for sleep for babies &amp; kids</>,
    featuredProduct: {
      handle: HATCH_PRODUCT_HANDLES[REST_KEY],
      intro: "Everybody sleeps better with Hatch Rest",
      image: {
        src: "dream-shop-category-rest-phone-appscreens-programs.png",
        alt: "Hatch Rest",
        sizes: { default: "80vw", tablet: "50vw" },
      },
      ...FEATURED_PRODUCTS[REST_KEY],
    },
    editorial: `
      We’re getting our babies back to sleep with a tap of the white noise.
      We’re eliminating bedtime battles with visual sleep cues for our toddlers.
      And we’re sleeping in past 7a.m. with a magical green light.
    `,
  },
};
